import { CancelButton, SaveButton } from '@core/components/Buttons';
import { SimpleDialog, SimpleDialogContent } from '@core/components/Dialog';
import { useApiUtils } from '@core/util/apiUtils';
import FuseUtils from '@fuse/utils/FuseUtils';
import LoadingModal from 'app/components/LoadingProgress/LoadingModal';
import { cancelarAlert } from 'app/services/alerts';
import { contratoApi } from 'app/services/api/contratoApi';
import * as Actions from 'app/store/actions';
import { FormikProvider, useFormik } from 'formik';
import moment from 'moment';
import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import SockJsClient from 'react-stomp';
import * as yup from 'yup';
import { ContaReceberContext } from '../../contaReceberContext';
import { ResgatarContaReceberContratoContext } from '../../resgatarContaReceberContratoContext';
import ResgatarContaReceberContratoTable from '../ResgatarContaReceberContratoTable';
import { contaReceberModais } from './contaReceberModais';

const rangeData = (mesInicial, mesFinal) => {
  if (mesInicial === null || mesFinal === null) {
    return true;
  }
  const yearDifference = moment(new Date(mesFinal)).diff(new Date(mesInicial), 'year', true);

  return !(yearDifference > 10);
};

const ResgatarContaReceberContratoSchema = yup.object().shape({
  dataVencimentoInicio: yup
    .date()
    .nullable()
    .test('dataVencimentoInicio', 'Informe um período de no máximo 10 anos', function(dataVencimentoInicio) {
      return rangeData(dataVencimentoInicio, this.parent?.dataVencimentoFim);
    }),
  dataVencimentoFim: yup
    .date()
    .nullable()
    .test('periodoFinal', 'Informe um período de no máximo 10 anos', function(dataVencimentoFim) {
      return rangeData(this.parent?.dataVencimentoInicio, dataVencimentoFim);
    }),
});

const ResgatarContaReceberContratoModal = ({ fetchContasReceber }) => {
  const [modal, setModal] = useContext(ContaReceberContext).modal;
  const [selecao, setSelecao] = useContext(ResgatarContaReceberContratoContext).selecao;
  const [tipoSelecao] = useContext(ResgatarContaReceberContratoContext).tipoSelecao;
  const [size, setSize] = useContext(ResgatarContaReceberContratoContext).size;
  const [idNotificacao, setIdNotificacao] = useState();
  const [, setContasReceber] = useContext(ResgatarContaReceberContratoContext).contasReceber;
  const [, setLoading] = useContext(ResgatarContaReceberContratoContext).loading;
  const [, setTotalElements] = useContext(ResgatarContaReceberContratoContext).totalElements;
  const [, setPage] = useContext(ResgatarContaReceberContratoContext).page;
  const [openLoadingModal, setOpenLoadingModal] = useState(false);
  const [progressoLoading, setProgressoLoading] = useState(0);

  const [contaReceberPage] = useContext(ContaReceberContext).page;
  const [contaReceberSize] = useContext(ContaReceberContext).size;
  const [contaReceberSort] = useContext(ContaReceberContext).sort;
  const [contaReceberSearch] = useContext(ContaReceberContext).search;
  const [filtroAvancado] = useContext(ContaReceberContext).filtroAvancado;
  const [, setVisualizarPendencias] = useContext(ContaReceberContext).visualizarPendencias;
  const [, setPendencias] = useContext(ContaReceberContext).pendencias;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const initialValues = {
    dataVencimentoInicio: moment().startOf('month'),
    dataVencimentoFim: moment().endOf('month'),
    identificador: '',
  };

  const initialPage = {
    page: 0,
    size,
    sort: 'dataVencimento,DESC',
  };

  const showError = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'error' }));
  };

  const itensSelecionados = (selecao && selecao.length > 0) || tipoSelecao === 'TODOS_FILTRO';

  const fetchReceitas = async (search = initialPage) => {
    setLoading(true);

    if (!search?.dataVencimentoInicio && !search?.dataVencimentoFim && !search?.identificador) {
      setLoading(false);
      throw apiUtils.handleApiError({
        message: 'Informe ao menos um filtro para realizar a busca',
      });
    } else {
      try {
        const resp = await contratoApi().buscarContasReceberContrato(search);
        setContasReceber(resp.data.content);
        setPage(resp.data.number);
        setSize(resp.data.size);
        setTotalElements(resp.data.totalElements);
        setSelecao([]);
      } catch (err) {
        apiUtils.handleApiError(err);
      }
    }
    setLoading(false);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: ResgatarContaReceberContratoSchema,
    onSubmit: () => handleResgatar(),
    validateOnChange: true,
    validateOnBlur: true,
  });

  const close = () => {
    setModal('');
    setContasReceber([]);
    formik.resetForm(initialValues);
  };

  const resgatarContasReceber = async (selecao, tipoSelecao, formik) => {
    const chaveNotificacao = FuseUtils.generateGUID();
    setIdNotificacao(chaveNotificacao);
    setProgressoLoading(0);
    setOpenLoadingModal(true);

    await contratoApi().resgatarContasReceberContrato(
      {
        tipoSelecao,
        ids: selecao?.map((item) => item?.contratoContaReceberId),
        dataVencimentoInicio: formik.values?.dataVencimentoInicio?.format('YYYY-MM-DD'),
        dataVencimentoFim: formik.values?.dataVencimentoFim?.format('YYYY-MM-DD'),
        identificador: formik.values?.identificador,
      },
      chaveNotificacao
    );
  };

  const handleResgatar = async () => {
    if (itensSelecionados) {
      setLoading(true);
      try {
        const { data: pendencias } = await contratoApi().verificarPendenciasCobrancaContrato({
          tipoSelecao,
          ids: selecao?.map((item) => item?.contratoContaReceberId),
          dataVencimentoInicio: formik.values?.dataVencimentoInicio?.format('YYYY-MM-DD'),
          dataVencimentoFim: formik.values?.dataVencimentoFim?.format('YYYY-MM-DD'),
          identificador: formik.values?.identificador,
        });

        if (pendencias?.length > 0) {
          setPendencias(pendencias);
          setVisualizarPendencias(true);
          setLoading(false);
        } else {
          await resgatarContasReceber(selecao, tipoSelecao, formik);
        }
      } catch (err) {
        apiUtils.handleApiError(err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlerMensagem = (notificacao) => {
    if (idNotificacao === notificacao.id) {
      if (isNaN(notificacao?.mensagem)) {
        setOpenLoadingModal(false);
        showError(notificacao.mensagem);
      } else {
        setProgressoLoading(notificacao?.mensagem);
        if (notificacao?.mensagem === '100.00' || notificacao?.mensagem === '100') {
          setOpenLoadingModal(false);
          close();

          fetchContasReceber({
            page: contaReceberPage,
            size: contaReceberSize,
            sort: contaReceberSort,
            q: contaReceberSearch,
            filtroAvancado,
          });
        }
      }
    }
  };

  const handleCancelar = () => {
    if (formik.dirty && itensSelecionados) {
      cancelarAlert({
        confirmar: close,
      });
    } else {
      close();
    }
  };

  return (
    <SimpleDialog open={modal === contaReceberModais.resgatarContaReceberContrato} onClose={handleCancelar}>
      <FormikProvider value={formik}>
        <SimpleDialogContent
          header={{
            title: 'Resgatar receitas do contrato',
            toolbar: (
              <>
                <CancelButton title="cancelar" type="button" onClick={handleCancelar}>
                  Cancelar
                </CancelButton>
                <SaveButton color="buttonSave" type="button" onClick={() => formik.handleSubmit(formik.values)}>
                  Resgatar
                </SaveButton>
              </>
            ),
          }}
          content={
            <>
              <ResgatarContaReceberContratoTable fetchReceitas={fetchReceitas} />
              <LoadingModal open={openLoadingModal} progress={progressoLoading} />
              <SockJsClient
                url={`${process.env.REACT_APP_WEBSOCKET_URL}`}
                topics={['/topic/notificacao']}
                onConnect={() => {
                  console.log('Connected');
                }}
                onDisconnect={() => {
                  console.log('Disconnected');
                }}
                onMessage={(notificacao) => {
                  handlerMensagem(notificacao);
                }}
              />
            </>
          }
          contentStyle={{ padding: 0 }}
        />
      </FormikProvider>
    </SimpleDialog>
  );
};

export default ResgatarContaReceberContratoModal;
